/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import { pluralize } from 'cio/helpers/pluralize';

import AnalyticsService from 'cio/services/analytics';
import EnvironmentService from 'cio/services/environment';
import TagModel from 'cio/models/tag';

type UsageTypeKey =
  | 'campaignsUsed'
  | 'newslettersUsed'
  | 'segmentsUsed'
  | 'apiTriggeredUsed'
  | 'transactionalMessagesUsed';

type UsageTypeModelName = 'Campaign' | 'Newsletter' | 'Segment' | 'Broadcast' | 'Transactional Message';

interface UsageType {
  key: UsageTypeKey;
  modelName: UsageTypeModelName;
}

interface TagEditorModalTableRowArgs {
  deleting: boolean;
  deletingTag?: TagModel;
  editingTag?: TagModel;
  tag: TagModel;
  tags: TagModel[];
  onDeleteTag: (tag: TagModel) => void;
  onSetDeleteTag: (tag?: TagModel) => void;
  onSetEditTag: (tag?: TagModel) => void;
  disabled: boolean;
}

export default class TagEditorModalTableRow extends Component<TagEditorModalTableRowArgs> {
  @service analytics!: AnalyticsService;
  @service environment!: EnvironmentService;

  @tracked editedTagName = '';

  usageTypes: UsageType[] = [
    {
      key: 'campaignsUsed',
      modelName: 'Campaign',
    },
    {
      key: 'apiTriggeredUsed',
      modelName: 'Broadcast',
    },
    {
      key: 'newslettersUsed',
      modelName: 'Newsletter',
    },
    {
      key: 'segmentsUsed',
      modelName: 'Segment',
    },
    {
      key: 'transactionalMessagesUsed',
      modelName: 'Transactional Message',
    },
  ];

  get usageCounts() {
    return this.usageTypes.reduce((usages: string[], type) => {
      let usageCount = this.args.tag[type.key];

      if (usageCount > 0) {
        usages.push(`${usageCount} ${pluralize([usageCount, type.modelName])}`);
      }

      return usages;
    }, []);
  }
  get usageCountsString() {
    let { usageCounts } = this;
    return usageCounts.length > 1
      ? `${usageCounts.slice(0, -1).join(', ')}, and ${usageCounts.slice(-1)}`
      : usageCounts[0] ?? '';
  }
  get isNameDirty() {
    return this.editedTagName.trim() !== this.args.tag.name.trim();
  }
  get isNameDuplicate() {
    return this.args.tags.map((tag) => tag.name.trim()).includes(this.editedTagName.trim());
  }
  get disabled() {
    let { disabled, editingTag, deletingTag, tag } = this.args;
    return disabled || (editingTag != null && tag !== editingTag) || (deletingTag != null && tag !== deletingTag);
  }

  @computed('editedTagName')
  get validationTooltipLabel() {
    if (!this.isNameDirty) {
      return 'There are no changes to save';
    } else if (this.isNameDuplicate) {
      return 'A tag with this name already exists';
    } else if (isEmpty(this.editedTagName)) {
      return 'Tag names cannot be blank';
    } else {
      return undefined;
    }
  }

  trackTagChangeStart(tag: TagModel, event: 'edit_started' | 'delete_started') {
    this.analytics.track('tags', event, {
      environmentId: this.environment.current.id,
      model: tag,
    });
  }

  saveTagTask = task(async (event: Event) => {
    event.preventDefault();

    let { tag } = this.args;

    tag.set('name', this.editedTagName);

    try {
      tag.save();
      this.args.onSetEditTag(undefined);
    } catch {
      tag.rollbackAttributes();
    }
  });

  @action
  discardTagChanges() {
    if (this.args.tag.get('isNew')) {
      this.analytics.track('tags', 'create_canceled');
    }

    this.args.onSetEditTag(undefined);
    this.args.tag.rollbackAttributes();
  }
  @action
  deleteTag() {
    let { tag } = this.args;
    this.trackTagChangeStart(tag, 'delete_started');
    this.args.onSetDeleteTag(tag);
  }
  @action
  editTag() {
    let { tag } = this.args;
    this.trackTagChangeStart(tag, 'edit_started');
    this.args.onSetEditTag(tag);
    this.editedTagName = tag.name;
  }
}
